import { StorageManager, Guess, Game, QUESTION_VALUE, TRIVIA_VALUE, GUESS_VALUE }  from './Common';
import { API_CONFIG } from '../configuration/api';
import StorageHelper from './storageHelper';

class LocalManager implements StorageManager {
    private game: Game;
    private gameName: string;
    private apiURL: string;

    constructor() {
        this.game = {
            firstname: '',
            lastname: '',
            gameid: '',
            gamedate: '',
            questions: {
                standard: [],
                unique: []
            },
            trivia: [],
            guesses: [],
            asks: [],
            triviaAsks: [],
            score: 0,
            gameCompleted: false
        }

        //generate gameName
        this.gameName = (new Date()).toLocaleDateString('en-US', { timeZone: 'America/Denver' });
        this.apiURL = API_CONFIG.getGamesUrl(false);
    }

    async loadGame(token?: string): Promise<Game> {
        try {
            // First, check if we have embedded game data from the script tag
            // This bypasses fetch API for Facebook browser compatibility
            const embeddedData = (window as any).__INITIAL_GAME_DATA__;
            if (embeddedData) {
                console.log('Using embedded game data (Facebook browser compatible)');
                this.game = {
                    ...embeddedData,
                    score: embeddedData.score ?? 0,
                    asks: embeddedData.asks ?? [],
                    triviaAsks: embeddedData.triviaAsks ?? [],
                    guesses: embeddedData.guesses ?? [],
                    trivia: embeddedData.trivia ?? [],
                    gameCompleted: embeddedData.gameCompleted ?? false
                };

                // Save to storage for future use
                try {
                    StorageHelper.setItem(this.gameName, JSON.stringify(this.game));
                } catch (storageError) {
                    console.warn('Could not save embedded data to storage:', storageError);
                }

                // Clear the global data to prevent re-use
                delete (window as any).__INITIAL_GAME_DATA__;
                return this.game;
            }

            // Check localStorage/in-memory storage
            const savedGame = StorageHelper.getItem(this.gameName);

            if (savedGame) {
                try {
                    this.game = JSON.parse(savedGame);
                } catch (parseError) {
                    console.error('Error parsing saved game:', parseError);
                    // Fall through to fetch from API
                }
            }

            // If no saved game or parse failed, fetch from API
            if (!savedGame || !this.game.gameid) {
                try {
                    const response = await fetch(this.apiURL, {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json'
                        }
                    });

                    if (!response.ok) {
                        const errorText = await response.text();
                        console.error(`API returned error: HTTP ${response.status}: ${response.statusText}`, errorText);
                        throw new Error(`HTTP ${response.status}: ${response.statusText} - ${errorText}`);
                    }

                    const contentType = response.headers.get('content-type');
                    if (!contentType || !contentType.includes('application/json')) {
                        const responseText = await response.text();
                        console.error('API returned non-JSON response:', contentType, responseText.substring(0, 200));
                        throw new Error(`Expected JSON response, got ${contentType}`);
                    }

                    const data = await response.json();

                    // Ensure the game has all required properties
                    this.game = {
                        ...data,
                        score: data.score ?? 0,
                        asks: data.asks ?? [],
                        triviaAsks: data.triviaAsks ?? [],
                        guesses: data.guesses ?? [],
                        trivia: data.trivia ?? [],
                        gameCompleted: data.gameCompleted ?? false
                    };

                    // Try to save to storage (may fail in restricted browsers)
                    try {
                        StorageHelper.setItem(this.gameName, JSON.stringify(this.game));
                    } catch (storageError) {
                        console.warn('Could not save to storage:', storageError);
                    }
                } catch (fetchError: any) {
                    const isFacebookBrowser = StorageHelper.isFacebookBrowser();
                    const isNetworkBlocked = fetchError.message === 'Load failed' || fetchError.message === 'Failed to fetch';

                    if (isFacebookBrowser && isNetworkBlocked) {
                        console.error('Facebook browser is blocking network requests. Open in external browser to play.');
                    } else {
                        console.error('Error fetching game from API:', fetchError.message);
                    }

                    // Keep the default/existing game
                }
            }
        } catch (error) {
            console.error('Unexpected error in loadGame:', error);
        }

        return this.game;
    }

    async recordQuestion(id: number, token?: string): Promise<number> {
        this.game.asks.push(id);
        this.game.score += QUESTION_VALUE;
        StorageHelper.setItem(this.gameName, JSON.stringify(this.game));
        return this.game.score;
    }

    async recordTrivia(id: number, token?: string): Promise<number> {
        this.game.triviaAsks.push(id);
        this.game.score += TRIVIA_VALUE;
        StorageHelper.setItem(this.gameName, JSON.stringify(this.game));
        return this.game.score;
    }

    async recordGuess(guess: Guess, token?: string): Promise<number> {
        this.game.guesses.push(guess);
        this.game.score += GUESS_VALUE;
        StorageHelper.setItem(this.gameName, JSON.stringify(this.game));
        return this.game.score;
    }

    // Method to update game completion status (for frontend to call when guess is correct)
    updateGameCompletion(completed: boolean): void {
        this.game.gameCompleted = completed;
        StorageHelper.setItem(this.gameName, JSON.stringify(this.game));
    }

    // For LocalManager, force reload just reloads from localStorage
    async forceReload(token?: string): Promise<Game> {
        return await this.loadGame(token);
    }
}

export default LocalManager;